import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import { Form, Input, Grid } from "antd";

import Button from "@/common/atoms/Button";
import LoginForm from "@/features/auth/login/LoginModal";
import { showModal } from "@/store/slices/modalSlice";
import GoogleLoginButton from "@/common/atoms/Button/GoogleLoginButton";
import { ErrorAlert } from "@/common";
import HomesGlobeSmallIcon from "@/assets/brand/HomesGlobeSmallIcon";
import { resetError, signupRequest } from "../duck/auth";
import BrnadInfoAuth from "../common/BrandInfoAuth";
import SignupFreeSpecialPromo from "../common/SignupFreeSpecialPromo";
import AuthHeading from "../common/AuthHeading";
import PPandTOU from "../common/PPandTOU";
import AuthOrDivider from "../common/AuthOrDivider";
import FormLabel from "../common/FormLabel";
import "./SignUpModal.scss";

const SignUpComponent = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { error } = useSelector((state) => state.auth, shallowEqual);

  const [form] = Form.useForm();
  const [passwordError, setPasswordError] = useState("");

  useEffect(() => {
    dispatch(resetError());
  }, [dispatch]);

  // TODO: refactor validation code
  // Password validation function
  const validatePassword = (value) => {
    const errors = [];
    if (value.length < 8) {
      errors.push(t("signUp.minLength"));
    }
    if (!/[A-Z]/.test(value)) {
      errors.push(t("signUp.uppercase"));
    }
    if (!/\d/.test(value)) {
      errors.push(t("signUp.number"));
    }
    if (/\s/.test(value)) {
      errors.push(t("signUp.noSpaces"));
    }
    if (errors.length > 0) {
      setPasswordError(`${t("signUp.errorPrefix")} ${errors.join(", ")}`);
      return false;
    } else {
      setPasswordError("");
      return true;
    }
  };

  const onFinish = (values) => {
    const payload = {
      userDto: {
        ...values
      }
    };
    dispatch(signupRequest(payload));
  };

  const showLoginModal = () => {
    dispatch(resetError());
    dispatch(
      showModal({
        content: <LoginForm />,
        classes: "login-modal-container"
      })
    );
  };

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const isMobile = !screens.lg;
  return (
    <div className="flex gap-1 relative sign-up-modal">
      <div className="bg-white rounded-lg p-8  flex-[2] relative sign-up-modal overflow-hidden flex flex-col justify-evenly">
        <HomesGlobeSmallIcon />
        <SignupFreeSpecialPromo />
        <AuthHeading
          title={t("common.signup")}
          subTitle={t("signUp.subTitle")}
          onClickTitle={t("signUp.signIn")}
          onClick={showLoginModal}
        />
        {error ? <ErrorAlert error={error} /> : null}
        <Form
          className="justify-between"
          form={form}
          name="signup"
          onFinish={onFinish}
          layout="vertical">
          <Form.Item
            name="email"
            label={<FormLabel label={t("common.email")} />}
            rules={[
              {
                required: true,
                message: t("common.emailRequired")
              },
              { type: "email", message: t("common.emailInvalid") }
            ]}>
            <Input
              placeholder={t("common.enterEmail")}
              className="rounded-md h-10 flex items-center justify-center"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={<FormLabel label={t("common.password")} />}
            rules={[
              { required: true, message: t("common.passwordRequired") },
              {
                validator: (_, value) => {
                  if (!validatePassword(value)) {
                    return Promise.reject(new Error(passwordError));
                  }
                  return Promise.resolve();
                }
              }
            ]}>
            <Input.Password
              placeholder={t("common.enterPassword")}
              iconRender={(visible) =>
                visible ? (
                  <EyeTwoTone twoToneColor="#7B9E7E" />
                ) : (
                  <EyeInvisibleOutlined />
                )
              }
              onChange={(e) => validatePassword(e.target.value)}
              className="rounded-md h-10 flex items-center justify-center "
            />
          </Form.Item>

          <Form.Item className="mb-0">
            <Button htmlType="submit" className="w-full h-[37px]">
              {t("common.signup")}
            </Button>
          </Form.Item>
        </Form>

        <AuthOrDivider title={t("common.or")} />
        <GoogleLoginButton />
        <PPandTOU />
      </div>
      {!isMobile && (
        <div className="flex-[1.5]  h-auto">
          <BrnadInfoAuth
            title={t("brandInfoAuth.title")}
            subTitle1={t("brandInfoAuth.subTitle1")}
            subTitle2={t("brandInfoAuth.subTitle2")}
          />
        </div>
      )}
    </div>
  );
};

export default SignUpComponent;
